import { Component, OnInit, Output, EventEmitter, ViewChild, Input } from '@angular/core';
import { AppropriatorsService, Appropriator } from 'src/app/services/appropriators.service';
import { PodsService, Pod } from 'src/app/services/pods.service';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from '@angular/forms';
import { trigger, style, transition, animate, keyframes } from '@angular/animations';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { ApplicationPackageService, ApplicationPackageWorkflowAction, WorkflowAction } from 'src/app/services/application-package.service';
import { PermitAnnualOperatingPlanWorkflowAction, PermitAopService } from 'src/app/services/permit-aop.service';
import { PolicyQueryService } from 'src/app/services/policy-query.service';
import { Reach, ReachService } from 'src/app/services/reach.service';
import { DblistService, DiversionType} from 'src/app/services/dblist.service';
import { WorkflowConfirmationModalComponent } from 'src/app/modules/shared/components/workflow-confirmation-modal/workflow-confirmation-modal.component';

const allowedTopRowAppPackageWorkflows: string[] = [ApplicationPackageWorkflowAction.ResetApplication, ApplicationPackageWorkflowAction.Withdraw, ApplicationPackageWorkflowAction.ReopenApplication];
const allowedTopRowAOPWorkflows: string[] = [PermitAnnualOperatingPlanWorkflowAction.ResetApplication, PermitAnnualOperatingPlanWorkflowAction.Withdraw, PermitAnnualOperatingPlanWorkflowAction.ReopenApplication];

type TotalWorkflowAction = {
  ApplicationPackageAction?: WorkflowAction;
  PermitAnnualOperatingPlanAction?: WorkflowAction;
  value: string;
  displayName: string;
}

@Component({
  selector: 'app-pod-form',
  animations: [
    trigger('openClose', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('0.25s', keyframes([
          style({ height: 0, opacity: 0, offset: 0 }),
          style({ height: '*', opacity: 1, offset: 1 }),
        ]))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, }),
        animate('0.25s', keyframes([
          style({ height: '*', opacity: 1, offset: 0 }),
          style({ height: 0, opacity: 0, offset: 1 }),
        ]))
      ])
    ])
  ],
  templateUrl: './pod-form.component.html',
  styleUrls: ['./pod-form.component.scss'],
  providers: [NgbModal, NgbModalConfig]
})
export class PodFormComponent implements OnInit {

  @ViewChild('f', {static: false}) form: NgForm;
  @ViewChild('podF', {static: false}) podForm: NgForm;

  @Input() url: string;

  selectedAppName: string;
  selectedAppAddress: string;
  selectedAppPhoneNumber: string;
  selectedAppEmail: string;
  selectedPodName: string;
  selectedPermitYear: number;
  selectedAppPackageType: string;

  listDiversionTypes: DiversionType[] = [];

  @Output() submitEvent = new EventEmitter<null>();

  appropriators: Appropriator[];
  canPostAppropriator: boolean;
  pods: Pod[];
  canPostPOD: boolean;
  submitted: boolean = false;
  reaches: Reach[];
  waterRightsId: string;
  applicationPackageId: number;

  currentPodAppId: number;
  currentPodAppName: string;
  editPOD: Pod;
  editPODTypeId: number = 0;

  topRowAppPackageWorkflows: WorkflowAction[] = [];
  topRowAOPWorkflows: WorkflowAction[] = [];
  topRowTotalWorkflows: TotalWorkflowAction[] = [];
  // allow use in template
  ApplicationPackageWorkflowAction = ApplicationPackageWorkflowAction;

  selectedPOD: Pod;
  selectedPodPriorYear: number;

  modalTitle = '';
  modalMessage = '';
  newAppMode = 1; //create blank; 2: create with a template
  podFormHandle: any;

  currentApplicationYear: number = 0;

  unsubscribe: Subject<any> = new Subject();

  constructor(
    private appropriatorsService: AppropriatorsService,
    private podsService: PodsService,
    private modalConfig: NgbModalConfig,
    private modalService: NgbModal,
    private applicationPackageService: ApplicationPackageService,
    private permitAopService: PermitAopService,
    private reachService: ReachService,
    private dbListService: DblistService,
    public policyQueryService: PolicyQueryService)
  { modalConfig.backdrop = 'static'; }

  ngOnInit() {
    this.appropriatorsService.getAppropriators();

    this.applicationPackageService.dssInstructions
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(dssinstructions => {
      if (dssinstructions) {
        this.currentApplicationYear = dssinstructions.applicationYear;
      }
    });

    this.appropriatorsService.currentAppropriators
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(appropriators => {
      this.appropriators = appropriators;
    });

    this.appropriatorsService.currentAppropriator
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(appropriator => {
      if(!!appropriator) {
        this.selectedAppName = appropriator.name;
        this.selectedAppAddress = appropriator.address;
        this.selectedAppPhoneNumber = appropriator.phoneNumber;
        this.selectedAppEmail = appropriator.email;
      }
    });

    /* Reaches for assignment to POD */
    this.reachService.getReaches();
    this.reachService.currentReaches
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(reaches => {
      this.reaches = reaches;
    });

    this.podsService.currentPods
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(pods => {
      this.pods = pods;
      this.newAppMode = undefined;
      if (this.pods.length > 0) {
        this.selectedPOD = this.pods[0];
        this.selectedPodPriorYear = this.selectedPOD.priorYearApplications[0] || null;
      }
      else {
        this.selectedPOD = null;
      }
    });

    this.podsService.currentPodByPodId
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(pod => {
      if (pod) {
        this.selectedPodName = pod.name;
      }
    });

    this.applicationPackageService.currentApplicationPackage
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(app => {
      if (app) {
        //this.selectedPermitYear = app.permitYear;
        this.selectedAppPackageType = app.permitTypeName;
        this.waterRightsId = app.waterRightsId;
        this.applicationPackageId = app.applicationPackageId;
        this.topRowAppPackageWorkflows = app.allowedWorkflowActions.filter(wfa => allowedTopRowAppPackageWorkflows.includes(wfa.value));
        this.recalcTotalWorkflows();
      }
    });

    this.permitAopService.currentAop
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(permitAop => {
        if (permitAop) {
          this.selectedPermitYear = permitAop.permitAnnualOperatingPlanYear;
          this.topRowAOPWorkflows = permitAop.allowedWorkflowActions.filter(wfa => allowedTopRowAOPWorkflows.includes(wfa.value));
          this.recalcTotalWorkflows();
        } else {
          if (this.currentApplicationYear > 0) {
            this.selectedPermitYear = this.currentApplicationYear;
          }
          else {
            this.selectedPermitYear = (new Date()).getFullYear();
          }
        }
      })

    this.policyQueryService.currentPolicyQuery
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(policyQuery => {
      if (policyQuery) {
        this.canPostAppropriator = policyQuery['api/Appropriator'].canAccess;
          this.canPostPOD = policyQuery['api/PermitPointOfDiversion'].canAccess;
      }
    });

    this.dbListService.getListDiversionType();
    this.dbListService.listDiversionType$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(dt => {
      this.listDiversionTypes = dt;
    });
    this.editPODTypeId = 0;
  }

  ngOnDestroy() {
    this.podsService.changePod(null);
    this.podsService.changePodByPodId([]);
    this.appropriatorsService.changeAppropriator(null);
    this.appropriatorsService.changeAppropriators([]);
    this.applicationPackageService.changeDssInstructions(null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  recalcTotalWorkflows() {
    // reset
    this.topRowTotalWorkflows = [];

    this.topRowAppPackageWorkflows.forEach(wfa => {
      this.topRowTotalWorkflows.push({
        ApplicationPackageAction: wfa,
        value: wfa.value,
        displayName: wfa.displayName
      });
    });
    this.topRowAOPWorkflows.forEach(wfa => {
      const existingWorkflow = this.topRowTotalWorkflows.find(trw => trw.value == wfa.value);
      if (existingWorkflow) {
        existingWorkflow.PermitAnnualOperatingPlanAction = wfa;
      } else {
        this.topRowTotalWorkflows.push({
          PermitAnnualOperatingPlanAction: wfa,
          value: wfa.value,
          displayName: wfa.displayName
        });
      }
    });
    this.topRowAOPWorkflows.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  doTotalWorkflowAction(action: TotalWorkflowAction) {
    const confirmationModal = this.modalService.open(WorkflowConfirmationModalComponent, { size: 'lg', centered: true });
    confirmationModal.componentInstance.modalTitle = `Confirm ${action.displayName}?`;
    if (action.value == ApplicationPackageWorkflowAction.Withdraw) {
      confirmationModal.componentInstance.modalBody = "This will withdraw your application from consideration. Click 'Confirm' to continue.";
    } else if (action.value == ApplicationPackageWorkflowAction.ResetApplication) {
      confirmationModal.componentInstance.modalBody = "This will clear all responses in your Variance, Application and Annual Operating Plan sections that are not approved. Click 'Confirm' to continue.";
    } else if (action.value == ApplicationPackageWorkflowAction.ReopenApplication) {
      confirmationModal.componentInstance.modalBody = "This will re-open your application. Click 'Confirm' to continue.";
    }
    confirmationModal.componentInstance.onConfirm.pipe(first()).subscribe(() => this.submitTotalAction(action));
  }

  submitTotalAction(action: TotalWorkflowAction) {
    if (action.ApplicationPackageAction != null) {
      this.applicationPackageService.submitPackage(ApplicationPackageWorkflowAction[action.value])
    }
    if (action.PermitAnnualOperatingPlanAction != null) {
      this.permitAopService.workflowAOP(PermitAnnualOperatingPlanWorkflowAction[action.value])
    }
  }

  podSubmit(form: any, event: Event) {
    this.submitEvent.emit();
    this.submitted = true;
    let appId = form.value.appropriator;
    let selectedAppId = +appId.substr(0, appId.indexOf('|'));
    let selectedApp = this.appropriators.filter(app => app.appropriatorId  == selectedAppId)[0];
    if (!selectedApp)
    {
      alert('Please select an Appropriator first.');
      return;
    }
    this.selectedAppName = selectedApp.name;
    let selectedPod = this.pods.filter(pod => pod.permitPointOfDiversionId == form.value.pod)[0];
    if (!selectedPod)
    {
      alert('Please select a Point of Diversion first.');
      return;
    }
    this.selectedPodName = selectedPod.name;
    //last check per creation mode
    if (selectedPod.priorYearApplications.length === 0 && this.newAppMode === 2) {
      alert('No previous year application exists at POD: ' + this.selectedPodName + "! Create a blank application instead.");
      this.modalService.dismissAll();
      return;
    }
    this.applicationPackageService.postAop(selectedApp.appropriatorId, selectedPod.permitPointOfDiversionId, this.newAppMode, this.selectedPodPriorYear);
    this.modalService.dismissAll();
  }

  selectPOD($event) {
    this.selectedPOD = this.pods.filter(pod => pod.permitPointOfDiversionId == +$event)[0];
    if (this.selectedPOD == null) return;
    this.selectedPodPriorYear = this.selectedPOD.priorYearApplications[0] || null;
  }

  onPodPriorYearSelected(priorYear: number) {
    this.selectedPodPriorYear = priorYear;
  }

  disableBlank() {
    let disabled = (!this.selectedPOD || this.selectedPOD.disabled === 'disabled');
    if (disabled) {
      /* Would like to uncheck the choice if it is disabled,
       * but afterwards, the radiobutton stopped displaying the "mark",
       * even though it is taking in user's choice!
       * */
      //var rbn = <HTMLInputElement>document.getElementById('newappblank');
      //rbn.checked = false;

      if (this.newAppMode === 1) {
        this.newAppMode = undefined;
      }
    }
    return disabled;
  }

  disableTemplate() {
    let disabled = (!this.selectedPOD || this.selectedPOD.priorYearApplications.length === 0 || this.selectedPOD.disabled === 'disabled');
    if (disabled) {
      /* See 'disableBlank()' comment, same dynamic, so comment out for now. */
      //var rbn = <HTMLInputElement>document.getElementById('newapptemplate');
      //rbn.checked = false;

      if (this.newAppMode === 2) {
        this.newAppMode = undefined;
      }
    }
    return disabled;
  }

  getPods(appId: string) {
    var id = +appId.substr(0, appId.indexOf('|'));
    this.currentPodAppId = id;
    this.currentPodAppName = appId.substr(appId.indexOf('|') + 1);
    if (this.currentApplicationYear > 0) {
      this.podsService.getPods(id, this.currentApplicationYear);
    } else {
      this.podsService.getPods(id);
    }
  }

  newPodSubmit(newPod) {
    this.podsService.postPod(this.currentPodAppId, newPod.value.newPodName,
      +newPod.value.newPodAppropriatorId,
      newPod.value.newPodLon,
      newPod.value.newPodLat,
      newPod.value.newPodProjectTypeId,
      +newPod.value.newPodDiversionTypeId,
      newPod.value.newPodSourceName,
      newPod.value.newPodDiversionCFSMax,
      newPod.value.newPodReachNumber);
    this.modalService.dismissAll();
  }

  updateEditDiversionTypeId($event) {
    if (!!$event) {
      this.editPODTypeId = 0;
      var name = $event.currentTarget.value;
      for (var i =0; i< this.listDiversionTypes.length; i++) {
        if (name === this.listDiversionTypes[i].name) {
          this.editPODTypeId = this.listDiversionTypes[i].diversionTypeId;
          break;
        }
      }
    }
  }

  editPodSubmit(editedPod)
  {
    var isPodDirty = false;
    var editCount = 0;
    var data : Pod = {
      permitPointOfDiversionId: this.editPOD.permitPointOfDiversionId,
      appropriatorId: 0,
      latitude: this.editPOD.latitude,
      longitude: this.editPOD.longitude,
      name: this.editPOD.name,
      diversionTypeId: this.editPOD.diversionTypeId,
      diversionTypeName: this.editPOD.diversionTypeName,
      sourceName: this.editPOD.sourceName,
      diversionCFSMax: this.editPOD.diversionCFSMax,
      reachNumber: this.editPOD.reachNumber,
      disabled: '',
      priorYearApplications: [],
      reachName: this.editPOD.reachName
    };
    if (this.editPOD.name !== editedPod.value.editPodName)
    {
      isPodDirty = true;
      editCount += 1;
      data.name = editedPod.value.editPodName;
      //console.log('this is edited: ' + this.editPOD.name + ' -> ' + editedPod.value.editPodName);
    }
    if (this.editPOD.sourceName !== editedPod.value.editPodSourceName)
    {
      isPodDirty = true;
      editCount += 1;
      data.sourceName = editedPod.value.editPodSourceName;
      //console.log('this is edited: ' + this.editPOD.sourceName + ' -> ' + editedPod.value.editPodSourceName);
    }
    if (this.editPOD.latitude !== +editedPod.value.editPodLat)
    {
      isPodDirty = true;
      editCount += 1;
      data.latitude = +editedPod.value.editPodLat;
      //console.log('this is edited: ' + this.editPOD.latitude + ' -> ' + editedPod.value.editPodLat);
    }
    if (this.editPOD.longitude !== +editedPod.value.editPodLon)
    {
      isPodDirty = true;
      editCount += 1;
      data.longitude = +editedPod.value.editPodLon;
      //console.log('this is edited: ' + this.editPOD.longitude + ' -> ' + editedPod.value.editPodLon);
    }
    if (this.editPOD.diversionTypeId !== this.editPODTypeId) //+editedPod.value.editPodDiversionTypeId)
    {
      isPodDirty = true;
      editCount += 1;
      data.diversionTypeId = this.editPODTypeId > 0? this.editPODTypeId : data.diversionTypeId;
      //console.log('this is edited: ' + this.editPOD.diversionTypeId + ' -> ' + editedPod.value.editPodDiversionTypeId);
    }
    if (this.editPOD.diversionCFSMax !== +editedPod.value.editPodDiversionCFSMax)
    {
      isPodDirty = true;
      editCount += 1;
      data.diversionCFSMax = +editedPod.value.editPodDiversionCFSMax;
      //console.log('this is edited: ' + this.editPOD.diversionCFSMax + ' -> ' + editedPod.value.editPodDiversionCFSMax);
    }
    if (this.editPOD.reachNumber !== +editedPod.value.editPodReachNumber)
    {
      isPodDirty = true;
      editCount += 1;
      data.reachNumber = +editedPod.value.editPodReachNumber;
      //console.log('this is edited: ' + this.editPOD.reachNumber + ' -> ' + editedPod.value.editPodReachNumber);
    }

    if (isPodDirty) {
      //console.log('this is edited, count = ' + editCount);
      this.podsService.putPod(this.currentPodAppId, data);
    }
    this.editPOD = null;
    this.editPODTypeId = 0;
    this.modalService.dismissAll();
  }

  open(content, label) {
    let size: any;
    if (label === 'modal-pod-table' || label === 'modal-app-table') {size = 'xl';}
    else {size = 'md';}
    this.modalService.open(content, {ariaLabelledBy: label, size: size});
  }

  genericModalShow(modal, title, message) {
    const activeModal = this.modalService.open(modal, { size: 'xl' });
    this.modalTitle = title;
    this.modalMessage = message;
  }

  reachNameDisplay(reach: Reach): string {
    if (reach.ReachInCalculation)
    {
      return `${reach.ReachName} (PWAP Reach ${reach.ReachNumber})`;
    }
    else
    {
      return `${reach.ReachName}`;
    }
  }
}
